import { useEffect } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { useListQuery } from "../lib/useListQuery";
import { isShowcaseMode } from "../lib/showcaseMode";
import {
  Badge,
  Button,
  EmptyState,
  FilterBar,
  Page,
  PageHeader,
  useToast,
  type FilterValue,
} from "../ui";
import {
  MARKER_FACETS,
  markerFiltersToQueryKey,
  type MarkerListQuery,
} from "./markerFacets";
import { PaginatedTableSection } from "./shared/PaginatedTableSection";
import { DateCell } from "./shared/DateCell";
import { NameWithId } from "./shared/NameWithId";
import {
  MarkerDetailDrawer,
  MarkerKindCell,
  MarkerReviewChip,
  formatEvaluator,
  formatOutput,
  useMarkerDrawerSubject,
  type MarkerDrawerSubject,
  type MarkerListNavigation,
} from "./MarkerDetailDrawer";
import {
  useMarkerRevisionAuthors,
  type MarkerRevisionHistoryResult,
} from "./MarkerRevisionHistory";
import { ShowcaseReadOnlyNotice } from "./ShowcaseReadOnlyNotice";

type MarkerResponse = components["schemas"]["MarkerResponse"];
type MarkerConfigurationItem = components["schemas"]["MarkerConfigurationItem"];
type MarkerConfigState = components["schemas"]["MarkerConfigState"];

export interface AgentMarkersViewProps {
  canLoadMore: boolean;
  drawer: MarkerDrawerSubject;
  filterValue?: FilterValue;
  isFetchingMore: boolean;
  isLoading: boolean;
  items: MarkerResponse[];
  listStateKey: string;
  history: MarkerRevisionHistoryResult;
  listError: string | null;
  // Server-authoritative built-in firing config, keyed by `builtin_key`. The
  // presence of an entry (i.e. the GET succeeded and returned it) is the
  // capability signal that gates the enable/disable toggle — never a client
  // role check. Empty when the caller cannot read the configuration.
  markerConfigByBuiltinKey?: Record<string, MarkerConfigurationItem>;
  navigation: MarkerListNavigation;
  onFilterChange?: (next: FilterValue) => void;
  onLoadMore: () => void;
  onToggleBuiltin?: (builtinKey: string, nextState: MarkerConfigState) => void;
  pendingBuiltinKey?: string | null;
}

function markerConfig(
  marker: MarkerResponse,
  markerConfigByBuiltinKey: Record<string, MarkerConfigurationItem>,
): MarkerConfigurationItem | undefined {
  return markerConfigByBuiltinKey[marker.builtin_key ?? marker.id];
}

// UI-LAW §4 DataTable / FilterBar uniformity: sortable-value columns carry
// accessors; `output`/`evaluator`/`origin` are the low-cardinality facets (the
// only URL filter on /markers is the root agent scope — no duplication).
function agentMarkerColumns(
  markerConfigByBuiltinKey: Record<string, MarkerConfigurationItem>,
): Array<ColumnDef<MarkerResponse>> {
  return [
    {
      header: "Name",
      id: "displayName",
      accessorFn: (marker) => marker.current_revision.display_name,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Marker ID"
          name={row.original.current_revision.display_name}
          strong
        />
      ),
    },
    {
      header: "Version",
      id: "version",
      accessorFn: (marker) => marker.current_revision.revision,
      cell: ({ row }) => String(row.original.current_revision.revision),
    },
    {
      header: "Enabled",
      cell: ({ row }) => (
        <MarkerStateCell
          config={markerConfig(row.original, markerConfigByBuiltinKey)}
        />
      ),
    },
    {
      header: "Output",
      id: "output",
      accessorFn: (marker) => formatOutput(marker.current_revision.output),
      cell: ({ row }) => (
        <Badge>{formatOutput(row.original.current_revision.output)}</Badge>
      ),
    },
    {
      header: "Evaluator",
      id: "evaluator",
      accessorFn: (marker) =>
        formatEvaluator(marker.current_revision.evaluator),
      cell: ({ row }) => (
        <Badge>
          {formatEvaluator(row.original.current_revision.evaluator)}
        </Badge>
      ),
    },
    // Hidden temporarily: locator values take too much horizontal table space.
    // Restore this column once the table can carry the information comfortably.
    // {
    //   header: "Locator",
    //   cell: ({ row }) => (
    //     <MarkerLocatorChip revision={row.original.current_revision} />
    //   ),
    // },
    {
      header: "Review",
      cell: ({ row }) => (
        <MarkerReviewChip revision={row.original.current_revision} />
      ),
    },
    {
      header: "Origin",
      id: "origin",
      accessorFn: (marker) =>
        marker.builtin_key == null ? "Custom" : "Built-in",
      cell: ({ row }) => <MarkerKindCell marker={row.original} />,
    },
    {
      header: "Updated",
      accessorKey: "updated_at",
      cell: ({ row }) => (
        <DateCell showYear={false} value={row.original.updated_at} />
      ),
    },
  ];
}

interface MarkerStateCellProps {
  config?: MarkerConfigurationItem;
}

// Passive firing-state cell (markers.md): the enable/disable control lives in
// the detail drawer, never in a row.
function MarkerStateCell({ config }: MarkerStateCellProps) {
  if (config == null) {
    return (
      <Badge title="The effective firing state is unavailable">
        Unavailable
      </Badge>
    );
  }
  const isOn = config.state === "on";
  return (
    <Badge tone={isOn ? "success" : "neutral"}>
      {isOn ? "Enabled" : "Disabled"}
    </Badge>
  );
}

export function AgentMarkersView({
  canLoadMore,
  drawer,
  filterValue = {},
  isFetchingMore,
  isLoading,
  items,
  listStateKey,
  history,
  listError,
  markerConfigByBuiltinKey = {},
  navigation,
  onFilterChange = () => undefined,
  onLoadMore,
  onToggleBuiltin,
  pendingBuiltinKey = null,
}: AgentMarkersViewProps) {
  const columns = agentMarkerColumns(markerConfigByBuiltinKey);
  const subjectConfig =
    drawer.subject !== undefined
      ? markerConfig(drawer.subject, markerConfigByBuiltinKey)
      : undefined;

  return (
    <Page variant="workspace">
      <PageHeader
        actions={
          isShowcaseMode ? (
            <ShowcaseReadOnlyNotice />
          ) : (
            <Button size="sm" onClick={navigation.onCreate}>
              Create marker
            </Button>
          )
        }
        leading={
          <FilterBar
            facets={MARKER_FACETS}
            onChange={onFilterChange}
            value={filterValue}
          />
        }
      />
      <PaginatedTableSection
        ariaLabel="Markers table"
        columns={columns}
        emptyState={<EmptyState title="No markers" />}
        enableSorting
        errorMessage={listError ?? undefined}
        getRowId={(marker) => marker.id}
        hasNextPage={canLoadMore}
        isFetchingNextPage={isFetchingMore}
        isPending={isLoading}
        items={items}
        listStateKey={listStateKey}
        onLoadMore={onLoadMore}
        onRowClick={(row) => {
          navigation.onOpenMarker(row.original.id);
        }}
        skeletonLabel="Loading markers"
        skeletonRows={5}
        virtual={{ fill: true, interactiveRows: true }}
      />
      <MarkerDetailDrawer
        builtinControl={{
          config: subjectConfig,
          onToggleBuiltin,
          pendingBuiltinKey,
        }}
        drawer={drawer}
        history={history}
        onAutomate={navigation.onAutomate}
        onDismiss={navigation.onCloseMarker}
        onRevise={navigation.onRevise}
      />
    </Page>
  );
}

interface BuiltinToggle {
  builtinKey: string;
  nextState: MarkerConfigState;
}

function markerConfigurationByBuiltinKey(
  items: MarkerConfigurationItem[] | undefined,
): Record<string, MarkerConfigurationItem> {
  const byKey: Record<string, MarkerConfigurationItem> = {};
  for (const item of items ?? []) {
    if (item.builtin_key != null) {
      byKey[item.builtin_key] = item;
    }
    byKey[item.marker_id] = item;
  }
  return byKey;
}

export interface AgentMarkersProps {
  agentId: string;
  filters?: MarkerListQuery;
  filterValue?: FilterValue;
  markerId?: string;
  navigation: MarkerListNavigation;
  onFilterChange?: (next: FilterValue) => void;
}

// Markers defined on one agent (docs/09-frontend-surface.md §4). The agent id
// is the active sidebar scope (a `?agent=` param), threaded in as a prop by
// the top-level Markers page. The List call passes `agent_id` as a query
// filter, never a path parent.
export function AgentMarkers({
  agentId,
  filters = {},
  filterValue = {},
  markerId,
  navigation,
  onFilterChange = () => undefined,
}: AgentMarkersProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  // The effective firing configuration is server-authoritative: a successful
  // GET is the capability signal that lets a member toggle built-ins.
  // A PERMISSION_DENIED (e.g. an API-key client, which is human-only-blocked)
  // simply yields no config, so no toggles render — no client role cast.
  const markerConfiguration = useQuery({
    queryKey: queryKeys.markerConfiguration.detail(agentId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/v1/agents/{id}:marker-configuration",
        { params: { path: { id: agentId } } },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    retry: false,
  });
  const markers = useListQuery("/v1/markers", {
    params: { agent_id: agentId, ...filters },
    queryKey: queryKeys.markers.list({
      agent_id: agentId,
      ...markerFiltersToQueryKey(filters),
    }),
  });
  const configureBuiltin = useMutation({
    mutationFn: async (directive: BuiltinToggle) => {
      const { data, error } = await apiClient.POST(
        "/v1/agents/{id}:configure-markers",
        {
          body: {
            markers: [
              {
                builtin_key: directive.builtinKey,
                marker_id: null,
                state: directive.nextState,
              },
            ],
          },
          params: { path: { id: agentId } },
        },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: (configuration, directive) => {
      // The write echoes the resulting configuration; adopt it directly.
      queryClient.setQueryData(
        queryKeys.markerConfiguration.detail(agentId),
        configuration,
      );
      toast.success(
        directive.nextState === "on" ? "Built-in enabled" : "Built-in disabled",
      );
    },
    onError: (error) => {
      toast.error("Could not update built-in", {
        description: requestErrorDescription(error, "Marker request failed."),
      });
    },
    // Belt-and-braces against echo reordering: even though the View
    // serializes toggles (all disabled while one is in flight), reconcile the
    // cache against a fresh GET after every settle so a stale echo can never
    // stick for the 30s staleTime window.
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.markerConfiguration.detail(agentId),
      });
    },
  });

  const drawer = useMarkerDrawerSubject(markerId, markers.items);
  const history = useListQuery("/v1/markers/{id}/revisions", {
    enabled: markerId !== undefined && drawer.subject?.builtin_key == null,
    pathParams: { id: markerId ?? "" },
    queryKey: queryKeys.markerRevisions.list({ marker_id: markerId }),
  });
  const resolvedHistory = useMarkerRevisionAuthors(history);
  const markerConfigByBuiltinKey = markerConfigurationByBuiltinKey(
    markerConfiguration.data?.markers,
  );

  // A resolved drawer subject that belongs to a DIFFERENT agent never renders
  // under this scope (markers.md URL state): strip the stale `?marker=`.
  const { onCloseMarker } = navigation;
  const subjectAgentId = drawer.subject?.agent_id;
  useEffect(() => {
    if (subjectAgentId != null && subjectAgentId !== agentId) {
      onCloseMarker();
    }
  }, [agentId, onCloseMarker, subjectAgentId]);

  return (
    <AgentMarkersView
      canLoadMore={markers.hasNextPage}
      drawer={drawer}
      filterValue={filterValue}
      isFetchingMore={markers.isFetchingNextPage}
      isLoading={markers.isPending}
      items={markers.items}
      listStateKey={markers.listStateKey}
      history={resolvedHistory}
      listError={markers.errorMessage ?? null}
      markerConfigByBuiltinKey={markerConfigByBuiltinKey}
      navigation={navigation}
      onFilterChange={onFilterChange}
      onLoadMore={markers.fetchNextPage}
      onToggleBuiltin={
        isShowcaseMode
          ? undefined
          : (builtinKey, nextState) => {
              configureBuiltin.mutate({ builtinKey, nextState });
            }
      }
      pendingBuiltinKey={
        configureBuiltin.isPending
          ? configureBuiltin.variables.builtinKey
          : null
      }
    />
  );
}
